import { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import { CreditCardIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

interface Invoice {
  id: number;
  invoice_number: string;
  client_name: string;
  amount_gross: number;
  currency: string;
}

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number | null;
  onPaymentAdded: () => void;
}

export const AddPaymentModal = ({ isOpen, onClose, invoiceId, onPaymentAdded }: AddPaymentModalProps) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: ''
  });
  const { showNotification } = useNotification();

  useEffect(() => {
    if (isOpen && invoiceId) {
      fetchInvoice();
    }
  }, [isOpen, invoiceId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/invoices/${invoiceId}`);
      setInvoice(response.data);
      // Ustaw domyślną kwotę jako pełną kwotę faktury
      setFormData(prev => ({
        ...prev,
        amount: Number(response.data.amount_gross || 0).toString()
      }));
    } catch (error) {
      console.error('Error fetching invoice:', error);
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: 'Nie udało się pobrać danych faktury'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoice) return;

    try {
      setLoading(true);
      
      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        currency: invoice.currency,
        status: 'completed'
      };

      console.log('Sending payment data:', paymentData);
      await apiService.post(`/invoices/${invoiceId}/payments`, paymentData);

      showNotification({
        type: 'success',
        title: 'Sukces',
        message: 'Płatność została dodana pomyślnie'
      });

      onPaymentAdded();
      onClose();
      
      // Reset form
      setFormData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        reference_number: '',
        notes: ''
      });
    } catch (error: any) {
      console.error('Error adding payment:', error);
      console.error('Error response:', error.response?.data);
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: error.response?.data?.message || 'Nie udało się dodać płatności'
      });
    } finally {
      setLoading(false);
    }
  };

  const paymentMethods = [
    { value: 'bank_transfer', label: 'Przelew bankowy' },
    { value: 'card', label: 'Karta płatnicza' },
    { value: 'cash', label: 'Gotówka' },
    { value: 'other', label: 'Inne' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <CreditCardIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Dodaj płatność
              </h3>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-4">
          {invoice && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Faktura: {invoice.invoice_number}</h4>
              <p className="text-sm text-gray-600">Klient: {invoice.client_name}</p>
              <p className="text-sm text-gray-600">
                Kwota faktury: {Number(invoice.amount_gross || 0).toFixed(2)} {invoice.currency}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kwota płatności
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data płatności
            </label>
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => handleInputChange('payment_date', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Metoda płatności
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) => handleInputChange('payment_method', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {paymentMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numer referencyjny
            </label>
            <input
              type="text"
              value={formData.reference_number}
              onChange={(e) => handleInputChange('reference_number', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Opcjonalny numer transakcji"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notatki
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Dodatkowe informacje o płatności"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Dodawanie...' : 'Dodaj płatność'}
          </Button>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
};